

















import { computed, defineComponent, onMounted, onUnmounted, ref, toRefs, watch } from "@vue/composition-api";
import { isEqual } from "lodash-es";
import { PaymentPackage, PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { TagInfoType } from "@scrile/api-provider/dist/api/TagsProvider";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import {
  ContentFeed,
  InputDiscoveryContentFeedSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useAuthUser from "@/hooks/useAuthUser";
import useContentFeed from "@/hooks/useContentFeed";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import usePager from "@/hooks/usePager";
import usePaymentPackage from "@/hooks/usePaymentPackage";
import useTags from "@/hooks/useTags";
import providers from "@/lib/api";
import { getPurchasedContentFeeds } from "@/lib/contentFeedHelper";
import emitter from "@/lib/emitter";
import { feedFields } from "@/helpers";
import config from "@/config";
import { CONTENT_FEED_SORTS, EVENTS, sortFields } from "@/const";
import DiscoverView from "./components/DiscoverView";

export default defineComponent({
  name: "PageDiscover",
  components: { DiscoverView },
  props: {
    onFeed: { type: Boolean, default: false },
    tagSlugs: { type: Array as () => string[], default: () => [] },
    sort: { type: String, default: "random" },
  },
  setup(props) {
    const { tagSlugs, sort } = toRefs(props);

    const { authUser, allowAccess } = useAuthUser();
    const { tagsBySlugIds, getTags, tagsBySlug: selectedTags } = useTags(tagSlugs);
    const { contentFeedDiscoverySearch } = useContentFeed();
    const { findAll: findPaymentPackages } = usePaymentPackage();
    const { showNotSafeForWork } = useNSFWUserSettings();

    const contentFeedUniqueUserIds = new Set();
    const loading = ref(false);
    const followedUsersId = ref<Record<string, boolean>>({});
    const paymentPackages = ref<PaymentPackage[]>([]);

    const generateRandomSeed = () => Math.round(Math.random() * 999999) + 1;

    const sortValue = computed(() => {
      if (!config.featureHashtags && config.featureTags) {
        return sortFields.find(field => field.slug === sort.value)?.sort ?? CONTENT_FEED_SORTS.RANDOM;
      }
      return CONTENT_FEED_SORTS.RANDOM;
    });

    const getTagFieldName = computed(() => (config.featureHashtags ? "postTagIds" : "userTagIds"));
    const tagsType = computed(() => (config.featureHashtags ? TagInfoType.POSTS : TagInfoType.USERS));

    const filterData = ref<InputDiscoveryContentFeedSearch>({
      randomSeed: generateRandomSeed(),
      safeForWork: showNotSafeForWork.value ? undefined : true,
      sort: sortValue.value,
      [getTagFieldName.value]: tagsBySlugIds.value.length ? tagsBySlugIds.value : undefined,
    });

    const { enableScroll, data: contentFeedSearchResult, getNextPage, getFirstPage, updateItem } = usePager<
      ContentFeed,
      InputDiscoveryContentFeedSearch
    >((page: number) => {
      return contentFeedDiscoverySearch(
        {
          page,
          ...filterData.value,
        },
        undefined,
        feedFields()
      );
    });

    const updateFollowedUsers = async () => {
      if (!authUser.value) return;
      const contentFeedUserIds = contentFeedSearchResult.value?.result.map((feed: ContentFeed) => feed.userId) || [];
      const newUserIds = contentFeedUserIds.filter(id => !contentFeedUniqueUserIds.has(id));

      if (!newUserIds.length) return;

      const newUniqueUserIds = newUserIds.filter((i, index) => newUserIds.indexOf(i) === index);
      const followerIds = (
        await providers.FollowingsProvider.searchFollowings({ ids: Array.from(newUniqueUserIds) })
      ).map(v => v.userId);
      const newFollowedUsersId = followerIds.reduce<Record<string, boolean>>((acc, cur) => {
        acc[cur] = true;
        return acc;
      }, {});
      followedUsersId.value = Object.assign(newFollowedUsersId, followedUsersId.value);
      newUniqueUserIds.forEach(userId => {
        contentFeedUniqueUserIds.add(userId);
      });
    };

    const handleToggleFollow = (userId: string, followStatus: boolean) => {
      followedUsersId.value = { ...followedUsersId.value, [userId]: followStatus };
    };

    const onContentFeedUpdate = (contentFeed: ContentFeed) => {
      updateItem(contentFeed, (i1, i2) => i1.id === i2.id);
    };

    const updateFeed = async (payload: { userId: string; feedId?: string }) => {
      if (!contentFeedSearchResult.value) return;
      try {
        contentFeedSearchResult.value = {
          ...contentFeedSearchResult.value,
          result: await getPurchasedContentFeeds(
            { authorId: payload.userId, purchasedFeedId: payload.feedId },
            contentFeedSearchResult.value,
            undefined,
            feedFields()
          ),
        };
      } finally {
        if (!payload.feedId) {
          handleToggleFollow(payload.userId, true);
        }
      }
    };

    const loadDiscovery = async () => {
      try {
        loading.value = true;
        await getFirstPage();
      } finally {
        loading.value = false;
      }
    };

    const loadDiscoveryWithFilter = (newFilterData: Partial<InputDiscoveryContentFeedSearch>) => {
      filterData.value = { ...filterData.value, ...newFilterData };
      loadDiscovery();
    };

    const getPaymentPackages = async () => {
      paymentPackages.value = await findPaymentPackages([PaymentPackageEnum.FILESET]);
    };

    const getData = async () => {
      await Promise.all([getPaymentPackages(), getTags(tagsType.value)]);
      watch(
        tagSlugs,
        (newValue, oldValue) => {
          if (!newValue?.length && !oldValue) {
            loadDiscovery();
            return;
          }
          if (!!oldValue && isEqual(newValue, oldValue)) return;
          loadDiscoveryWithFilter({
            [getTagFieldName.value]: tagsBySlugIds.value.length ? tagsBySlugIds.value : undefined,
          });
        },
        { immediate: true }
      );
    };

    getData();

    watch(authUser, (user: User | null, oldUser: User | null) => {
      if (!oldUser && user && user.accountInfo.lastLoginTime && allowAccess.value) {
        loadDiscovery().then(() => window.scrollTo({ top: 0 }));
      }
    });

    watch(showNotSafeForWork, async (newVal, oldVal) => {
      if (oldVal === newVal) return;
      loadDiscoveryWithFilter({
        safeForWork: showNotSafeForWork.value ? undefined : true,
      });
    });

    watch(contentFeedSearchResult, async (newVal, oldVal) => {
      if (oldVal === newVal) return;
      await updateFollowedUsers();
    });

    watch(sort, async (newVal, oldVal) => {
      if (oldVal === newVal) return;
      loadDiscoveryWithFilter({
        sort: sortValue.value,
        randomSeed: newVal === "random" ? generateRandomSeed() : undefined,
      });
    });

    onMounted(async () => {
      emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
    });

    onUnmounted(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
    });

    return {
      loading,
      enableScroll,
      paymentPackages,
      contentFeedSearchResult,
      getNextPage,
      followedUsersId,
      filterData,
      tagsType,
      selectedTags,
      sortValue,
      handleToggleFollow,
      onContentFeedUpdate,
    };
  },
});
